import { useEffect } from 'react'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Button } from '@mui/material'
import { MdAdd, MdDelete, MdRemove } from 'react-icons/md'

import { Flex, Heading } from '@/ui'
import { useRouter } from '@/hooks/router'

import { useOrderCreateStore } from '@/stores/orders/create'
import {
    getNumberFormatted,
    getPriceFormatted,
    getWeightFormatted,
} from '@/utils/app/number'
import { urls } from '@/utils/constants'
import { getPriceTotal } from '@/utils/konvives/cart'
import { pathWithId } from '@/utils/router'

import * as styles from './CartSidebar.css'

/** Display the sidebar cart. */
export const CartSidebar = (): JSX.Element | null => {
    const { pathname } = useLocation()
    const router = useRouter()
    const { t } = useTranslation()

    const articles = useOrderCreateStore((state) => state.articles)

    const canDisplayCartSidebar = useOrderCreateStore(
        (state) => state.canDisplayCartSidebar,
    )

    const updateCanDisplayCartSidebar = useOrderCreateStore(
        (state) => state.updateCanDisplayCartSidebar,
    )

    const updateArticles = useOrderCreateStore((state) => state.updateArticles)

    const id = 'cart_sidebar'

    // Handle displaying cart sidebar
    useEffect(() => {
        // Close the sidebar when clicking outside
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Element

            const cartSidebarElement = document.querySelector(`#${id}`)

            if (cartSidebarElement && !cartSidebarElement.contains(target)) {
                updateCanDisplayCartSidebar(false)
            }
        }

        window.addEventListener('click', handleClickOutside, {
            passive: true,
        })

        return () => {
            window.removeEventListener('click', handleClickOutside)
        }
    }, [canDisplayCartSidebar, pathname, updateCanDisplayCartSidebar])

    return (
        <div
            id={id}
            className={styles.root}
        >
            {/* Header */}
            <Flex
                className={styles.header}
                column
            >
                <Heading center>{t('cart')}</Heading>

                <div className={styles.total}>{getPriceTotal(articles)}</div>

                <Button
                    variant='contained'
                    className={styles.buttonCart}
                    onClick={() => {
                        router.push(urls.CART)
                        updateCanDisplayCartSidebar(false)
                    }}
                >
                    {t('goToCart')}
                </Button>
            </Flex>

            {/* Articles */}
            <div className={styles.content}>
                {articles.map((article) => (
                    <Flex
                        key={article.product.id}
                        className={styles.article}
                        column
                    >
                        {/* Article */}
                        <Button
                            className={styles.buttonProduct}
                            onClick={() => {
                                router.push(
                                    pathWithId(
                                        urls.catalogue.products.DETAILS,
                                        article.product.id,
                                    ),
                                )
                            }}
                        >
                            {/* Name */}
                            <div>{article.product.name}</div>

                            <Flex>
                                {/* Image */}
                                <div className={styles.imageBox}>
                                    <img
                                        className={styles.image}
                                        src={article.product.photo}
                                        alt={article.product.name}
                                    />
                                </div>

                                {/* Price */}
                                <div>
                                    {!!article.product.is_price_weight &&
                                        `${getPriceFormatted(article.product.price)} / Kg (~ ${getWeightFormatted(article.product.weight)} / U)`}

                                    {!article.product.is_price_weight &&
                                        getPriceFormatted(
                                            article.product.price,
                                        )}
                                </div>
                            </Flex>
                        </Button>

                        {/* Quantity and actions */}
                        <Flex className={styles.action}>
                            {/* Delete */}
                            <Button
                                variant='contained'
                                className={styles.buttonAction}
                                onClick={() => {
                                    const articlesFiltered = structuredClone(
                                        articles,
                                    ).filter(
                                        (articleToTest) =>
                                            articleToTest.product.id !==
                                            article.product.id,
                                    )

                                    updateArticles(articlesFiltered)
                                }}
                            >
                                <MdDelete />
                            </Button>

                            {/* Remove quantity */}
                            <Button
                                variant='contained'
                                className={styles.buttonAction}
                                onClick={() => {
                                    const articlesCloned =
                                        structuredClone(articles)

                                    const articleFound = articlesCloned.find(
                                        (articleToTest) =>
                                            articleToTest.product.id ===
                                            article.product.id,
                                    )

                                    if (articleFound) {
                                        articleFound.quantity -= 1
                                    }

                                    updateArticles(articlesCloned)
                                }}
                                disabled={article.quantity === 1}
                            >
                                <MdRemove />
                            </Button>

                            {/* Quantity*/}
                            <div>
                                {/* Not price weight */}
                                {!article.is_weight_quantity &&
                                    !article.product.is_price_weight &&
                                    getNumberFormatted(article.quantity)}

                                {/* Price weight by weight */}
                                {!!article.is_weight_quantity &&
                                    getWeightFormatted(article.quantity)}

                                {/* Price weight by unit */}
                                {!article.is_weight_quantity &&
                                    !!article.product.is_price_weight &&
                                    `${getNumberFormatted(article.quantity)} (~ ${getWeightFormatted(article.quantity * article.product.weight)})`}
                            </div>

                            {/* Add quantity */}
                            <Button
                                variant='contained'
                                className={styles.buttonAction}
                                onClick={() => {
                                    const articlesCloned =
                                        structuredClone(articles)

                                    const articleFound = articlesCloned.find(
                                        (articleToTest) =>
                                            articleToTest.product.id ===
                                            article.product.id,
                                    )

                                    if (articleFound) {
                                        articleFound.quantity += 1
                                    }

                                    updateArticles(articlesCloned)
                                }}
                            >
                                <MdAdd />
                            </Button>
                        </Flex>
                    </Flex>
                ))}
            </div>
        </div>
    )
}
